/**
 * ORG-WIDE FAIL ALERT (Plano fail protocol, field feedback item 5).
 *
 * When a device fails in a fail-protocol jurisdiction the tech is idle on
 * site with the water off while the office reacts — seconds matter. This
 * banner renders on EVERY office page (mounted in the app shell), polls the
 * active-alerts endpoint, and stays up until the quote for that device is
 * SENT (that's the office's job in the chain). Subsequent failures increment
 * visibly ("2nd failure today"). Snooze is per-user, per-alert, 15 minutes.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

interface FailAlert {
    id: string;
    job_id: string;
    created_at: string;
    provider_name: string | null;
    site_name: string | null;
    device: string;
    failure_index_today: number;
}

const SNOOZE_MS = 15 * 60 * 1000;
const snoozeKey = (id: string) => `fail-alert-snooze:${id}`;

function snoozedUntil(id: string): number {
    const raw = localStorage.getItem(snoozeKey(id));
    const until = raw ? Number(raw) : 0;
    return Number.isFinite(until) ? until : 0;
}

function ordinal(n: number): string {
    const rem10 = n % 10;
    const rem100 = n % 100;
    if (rem10 === 1 && rem100 !== 11) return `${n}st`;
    if (rem10 === 2 && rem100 !== 12) return `${n}nd`;
    if (rem10 === 3 && rem100 !== 13) return `${n}rd`;
    return `${n}th`;
}

export function FailProtocolAlertBanner() {
    const { can } = useAuth();
    const allowed = can("finance.view");
    // Bumped on snooze and every 30s so expired snoozes resurface without a
    // reload and fresh snoozes hide immediately.
    const [, bump] = useState(0);
    useEffect(() => {
        const t = setInterval(() => bump((x) => x + 1), 30_000);
        return () => clearInterval(t);
    }, []);

    const alertsQ = useQuery({
        queryKey: ["fail-protocol-alerts"],
        queryFn: () => api.get<{ alerts: FailAlert[]; failures_today: number }>("/api/fail-protocol/alerts/active"),
        enabled: allowed,
        refetchInterval: 15_000,
    });

    if (!allowed) return null;
    const now = Date.now();
    const alerts = (alertsQ.data?.alerts ?? []).filter((a) => snoozedUntil(a.id) <= now);
    if (alerts.length === 0) return null;

    return (
        <div
            className="border-b border-red-800 bg-red-600 text-white shadow-md"
            role="alert"
            data-testid="fail-protocol-alert-banner"
        >
            {alerts.map((a) => (
                <div key={a.id} className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 py-2 text-sm">
                    <AlertTriangle className="h-4 w-4 shrink-0 animate-pulse" />
                    <span className="min-w-0 flex-1 font-medium">
                        Device failed in {a.provider_name ?? "a protocol city"} @ {a.site_name ?? "site"} — water off,
                        tech waiting
                        <span className="ml-1 font-normal text-red-100">({a.device})</span>
                        {a.failure_index_today > 1 && (
                            <span className="ml-2 rounded bg-white/25 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide">
                                {ordinal(a.failure_index_today)} failure today
                            </span>
                        )}
                    </span>
                    <Button size="sm" variant="secondary" asChild className="h-7">
                        <Link to={`/jobs/${a.job_id}`}>Look into</Link>
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-white hover:bg-white/15 hover:text-white"
                        onClick={() => {
                            localStorage.setItem(snoozeKey(a.id), String(Date.now() + SNOOZE_MS));
                            bump((x) => x + 1);
                        }}
                    >
                        <Clock className="mr-1 h-3.5 w-3.5" />
                        Snooze 15m
                    </Button>
                </div>
            ))}
        </div>
    );
}
